import styled, { css } from 'styled-components';
import {
  TOOLTIP_ALIGN_CENTER,
  TOOLTIP_ALIGN_END,
  TOOLTIP_ALIGN_START,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_PLACEMENT_LEFT,
  TOOLTIP_PLACEMENT_RIGHT,
  TOOLTIP_PLACEMENT_TOP,
  TOOLTIP_SIZE_MEDIUM,
  TOOLTIP_VIEWPORT_MARGIN_PX,
  type TooltipAlign,
  type TooltipPlacement,
  type TooltipSize,
} from './Tooltip.constants';

interface WrapperProps {
  /**
   * When true, the wrapper and the inner trigger span become block-level
   * and fill their parent's width. Lets callers wrap a child that owns
   * its own `width: 100%` (e.g. a dropdown row button) without the
   * default `inline-flex` collapsing it to content width.
   */
  $fullWidth?: boolean;
}

export const Wrapper = styled.span<WrapperProps>`
  position: relative;
  display: ${({ $fullWidth }) => ($fullWidth ? 'block' : 'inline-flex')};
  align-items: center;
  ${({ $fullWidth }) => ($fullWidth ? 'width: 100%;' : '')}
`;

/**
 * Inner wrapper that carries the trigger ref + `aria-describedby` link.
 * Stays inline by default but goes block + full-width when the parent
 * `Wrapper` does, so the actual child (e.g. a `width: 100%` button)
 * resolves its percentage against the menu/container rather than its
 * own content width.
 */
export const TriggerSpan = styled.span<WrapperProps>`
  ${({ $fullWidth }) => ($fullWidth ? 'display: block; width: 100%;' : '')}
`;

interface TipProps {
  $placement: TooltipPlacement;
  $align: TooltipAlign;
  $size: TooltipSize;
  $isOpen: boolean;
  /**
   * When true the tip renders via React portal with `position: fixed`
   * and inline `top`/`left` projected from the trigger's bounding rect
   * — see Tooltip.tsx. We then only emit transforms here (cross-axis
   * anchoring + 2px slide-in animation), not the wrapper-anchored offsets.
   */
  $portal?: boolean;
}

interface CrossAxis {
  /** Offset of the tip's anchor point along the trigger's cross axis. */
  anchor: string;
  /** Translate that pulls the tip's own edge back onto that anchor point. */
  shift: string;
}

const crossAxisOf = (align: TooltipAlign): CrossAxis => {
  switch (align) {
    case TOOLTIP_ALIGN_START:
      return { anchor: '0', shift: '0' };
    case TOOLTIP_ALIGN_CENTER:
      return { anchor: '50%', shift: '-50%' };
    case TOOLTIP_ALIGN_END:
      return { anchor: '100%', shift: '-100%' };
    default: {
      const exhaustive: never = align;
      throw new Error(`Unknown tooltip align: ${String(exhaustive)}`);
    }
  }
};

const unknownPlacement = (placement: never): never => {
  throw new Error(`Unknown tooltip placement: ${String(placement)}`);
};

const placementCss = ($placement: TooltipPlacement, { anchor, shift }: CrossAxis) => {
  switch ($placement) {
    case TOOLTIP_PLACEMENT_TOP:
      return css`
        bottom: calc(100% + 6px);
        left: ${anchor};
        transform: translate(${shift}, -2px);
      `;
    case TOOLTIP_PLACEMENT_BOTTOM:
      return css`
        top: calc(100% + 6px);
        left: ${anchor};
        transform: translate(${shift}, -2px);
      `;
    case TOOLTIP_PLACEMENT_LEFT:
      return css`
        right: calc(100% + 6px);
        top: ${anchor};
        transform: translate(-2px, ${shift});
      `;
    case TOOLTIP_PLACEMENT_RIGHT:
      return css`
        left: calc(100% + 6px);
        top: ${anchor};
        transform: translate(-2px, ${shift});
      `;
    default:
      return unknownPlacement($placement);
  }
};

const placementOpenCss = ($placement: TooltipPlacement, { shift }: CrossAxis) => {
  switch ($placement) {
    case TOOLTIP_PLACEMENT_TOP:
    case TOOLTIP_PLACEMENT_BOTTOM:
      return css`
        transform: translate(${shift}, 0);
      `;
    case TOOLTIP_PLACEMENT_LEFT:
    case TOOLTIP_PLACEMENT_RIGHT:
      return css`
        transform: translate(0, ${shift});
      `;
    default:
      return unknownPlacement($placement);
  }
};

/**
 * Portal mode: `top` / `left` are written inline by the Tooltip component
 * from the trigger's bounding rect, so only the transform is emitted here.
 */
const placementCssPortal = ($placement: TooltipPlacement, { shift }: CrossAxis) => {
  switch ($placement) {
    case TOOLTIP_PLACEMENT_TOP:
      return css`
        transform: translate(${shift}, calc(-100% - 2px));
      `;
    case TOOLTIP_PLACEMENT_BOTTOM:
      return css`
        transform: translate(${shift}, -2px);
      `;
    case TOOLTIP_PLACEMENT_LEFT:
      return css`
        transform: translate(calc(-100% - 2px), ${shift});
      `;
    case TOOLTIP_PLACEMENT_RIGHT:
      return css`
        transform: translate(2px, ${shift});
      `;
    default:
      return unknownPlacement($placement);
  }
};

const placementOpenCssPortal = ($placement: TooltipPlacement, { shift }: CrossAxis) => {
  switch ($placement) {
    case TOOLTIP_PLACEMENT_TOP:
      return css`
        transform: translate(${shift}, -100%);
      `;
    case TOOLTIP_PLACEMENT_BOTTOM:
      return css`
        transform: translate(${shift}, 0);
      `;
    case TOOLTIP_PLACEMENT_LEFT:
      return css`
        transform: translate(-100%, ${shift});
      `;
    case TOOLTIP_PLACEMENT_RIGHT:
      return css`
        transform: translate(0, ${shift});
      `;
    default:
      return unknownPlacement($placement);
  }
};

export const Tip = styled.span<TipProps>`
  position: ${({ $portal }) => ($portal ? 'fixed' : 'absolute')};
  width: max-content;
  max-width: min(260px, calc(100vw - ${2 * TOOLTIP_VIEWPORT_MARGIN_PX}px));
  padding: 8px 10px;
  border-radius: ${({ theme: { borderRadius = {} } }) => borderRadius.small ?? '8px'};
  font-size: 12px;
  line-height: 1.4;
  font-weight: 400;
  white-space: normal;
  pointer-events: none;
  z-index: 1500;
  transition: opacity ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'},
    transform ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'};

  ${({ theme: { colors = {}, elevation = {} } }) => css`
    background: ${colors.surfaceContainer ?? '#1F1F27'};
    color: ${colors.onSurface ?? '#E4E1ED'};
    border: 1px solid ${colors.outlineVariant ?? '#464555'};
    box-shadow: ${elevation.level2 ??
    '0 1px 2px 0 rgb(0 0 0 / 0.30), 0 2px 6px 2px rgb(0 0 0 / 0.15)'};
  `}

  ${({ $size, theme: { borderRadius = {} } }) =>
    $size === TOOLTIP_SIZE_MEDIUM &&
    css`
      max-width: min(360px, calc(100vw - ${2 * TOOLTIP_VIEWPORT_MARGIN_PX}px));
      padding: 12px 16px;
      border-radius: ${borderRadius.large ?? '16px'};
      font-size: 15px;
    `}

  ${({ $placement, $align, $portal }) =>
    $portal
      ? placementCssPortal($placement, crossAxisOf($align))
      : placementCss($placement, crossAxisOf($align))}

  opacity: 0;

  ${({ $isOpen, $placement, $align, $portal }) =>
    $isOpen &&
    css`
      opacity: 1;
      ${$portal
        ? placementOpenCssPortal($placement, crossAxisOf($align))
        : placementOpenCss($placement, crossAxisOf($align))}
    `}
`;
